/**
 * WatchAndShop.jsx
 *
 * BEHAVIOUR:
 *  1. Section scrolls into view → all 5 cards do a 3D "pop" entrance (GSAP).
 *  2. All videos initially start paused, showing only the thumbnail and video play button.
 *  3. Thumbnail stays visible while the video is paused.
 *  4. Each card shows a Pause button while playing, Play button while paused.
 *     Clicking the button toggles that card's inline video. Does NOT open lightbox.
 *  5. Clicking anywhere on the card (not the button) opens GSAP lightbox with
 *     full audio + controls.
 *  6. When section leaves viewport any currently playing videos pause (IntersectionObserver).
 */

import React, {
  useState, useEffect, useRef, useCallback, memo,
} from "react";
import { Play, Pause, X, Volume2, VolumeX, ChevronLeft, ChevronRight } from "lucide-react";
import Lottie from "lottie-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getWatchAndShopVideos } from "../../../api/watchAndShopApi";

gsap.registerPlugin(ScrollTrigger, useGSAP);

/* ── Lottie pulse ring (inline, no external file) ─────────────────────── */
const PULSE_LOTTIE = {
  v: "5.7.4", fr: 30, ip: 0, op: 60, w: 120, h: 120, nm: "pulse",
  assets: [],
  layers: [{
    ddd: 0, ind: 1, ty: 4, nm: "ring", sr: 1,
    ks: {
      o: { a: 1, k: [{ t: 0, s: [50], e: [0] }, { t: 60, s: [0] }] },
      s: { a: 1, k: [{ t: 0, s: [100, 100, 100], e: [220, 220, 100] }, { t: 60, s: [220, 220, 100] }] },
      p: { a: 0, k: [60, 60, 0] }, a: { a: 0, k: [0, 0, 0] }, r: { a: 0, k: 0 },
    },
    shapes: [{
      ty: "gr", it: [
        { ty: "el", s: { a: 0, k: [48, 48] }, p: { a: 0, k: [0, 0] } },
        { ty: "st", c: { a: 0, k: [1, 1, 1, 1] }, o: { a: 0, k: 100 }, w: { a: 0, k: 2 }, lc: 2, lj: 2 },
        { ty: "fl", c: { a: 0, k: [1, 1, 1, 0] }, o: { a: 0, k: 0 } },
        { ty: "tr", p: { a: 0, k: [0, 0] }, a: { a: 0, k: [0, 0] }, s: { a: 0, k: [100, 100] }, r: { a: 0, k: 0 }, o: { a: 0, k: 100 } },
      ],
    }],
    ip: 0, op: 60, st: 0,
  }],
};

/* ── Shimmer skeleton ─────────────────────────────────────────────────── */
const Shimmer = memo(({ hero }) => (
  <div className={`was-shimmer ${hero ? "was-hero-cell" : ""}`}>
    <div className="was-shimmer-slide" />
  </div>
));
Shimmer.displayName = "Shimmer";

/* ── Duration badge ───────────────────────────────────────────────────── */
const DurationBadge = memo(({ dur }) =>
  dur ? <div className="was-dur-badge">{dur}</div> : null
);
DurationBadge.displayName = "DurationBadge";

/* ────────────────────────────────────────────────────────────────────────
   VIDEO CARD
   - autoplay = section-level signal passed from parent
   - vidRef   = forwarded from parent so section IntersectionObserver can
                play/pause all videos at once
──────────────────────────────────────────────────────────────────────── */
const VideoCard = memo(({ video, hero, onClick, autoplay, vidRef }) => {
  const cardRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [durationStr, setDurationStr] = useState(video.duration || "");


  /* Magnetic hover */
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width  - 0.5) * 10;
      const y = ((e.clientY - rect.top)  / rect.height - 0.5) * 10;
      gsap.to(el, { rotateX: -y, rotateY: x, duration: 0.4, ease: "power2.out" });
    };
    const onLeave = () => gsap.to(el, { rotateX: 0, rotateY: 0, duration: 0.7, ease: "elastic.out(1, 0.5)" });
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => { el.removeEventListener("mousemove", onMove); el.removeEventListener("mouseleave", onLeave); };
  }, []);

  /* Pause video if section leaves viewport (do not autoplay initially) */
  useEffect(() => {
    const vid = vidRef.current;
    if (!vid) return;
    if (!autoplay && isPlaying) {
      vid.pause();
      setIsPlaying(false);
    }
  }, [autoplay, vidRef, isPlaying]);

  /* Toggle button — stops propagation so it doesn't open lightbox */
  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    const vid = vidRef.current;
    if (!vid) return;
    if (isPlaying) {
      vid.pause();
      setIsPlaying(false);
    } else {
      vid.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying, vidRef]);

  return (
    <div
      ref={cardRef}
      className={`was-card group ${hero ? "was-hero-cell" : "was-small-cell"}`}
      onClick={() => onClick(video)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(video)}
      aria-label={`Open video: ${video.title || "Watch video"}`}
    >
      {/* ── Thumbnail (visible when video is paused or before play) ── */}
      {video.thumbnailUrl ? (
        <img
          src={video.thumbnailUrl}
          alt={video.title || "Video thumbnail"}
          loading="lazy"
          onLoad={() => setThumbLoaded(true)}
          className="was-media"
          style={{ opacity: !isPlaying ? (thumbLoaded ? 1 : 0) : 0 }}
        />
      ) : (
        <div
          className="was-media bg-gray-200 dark:bg-[#222]"
          style={{
            opacity: !isPlaying ? 1 : 0,
          }}
        />
      )}

      {/* ── Inline video preview (visible while playing) ── */}
      {video.videoUrl && (
        <video
          ref={vidRef}
          muted
          playsInline
          loop
          preload="metadata"
          onCanPlay={() => setVideoReady(true)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedMetadata={(e) => {
            if (!video.duration && e.target.duration && !isNaN(e.target.duration) && e.target.duration !== Infinity) {
              const d = e.target.duration;
              const m = Math.floor(d / 60);
              const s = Math.floor(d % 60);
              setDurationStr(`${m}:${s < 10 ? '0' : ''}${s}`);
            }
          }}
          className="was-media"
          style={{ opacity: isPlaying ? 1 : (video.thumbnailUrl ? 0 : 1) }}
        >
          <source src={video.videoUrl} />
        </video>
      )}

      {/* ── Bottom gradient ── */}
      <div className="was-gradient-overlay" />

      {/* ── Scanline overlay (reveals on hover) ── */}
      <div className="was-scanline" />

      {/* ── Duration badge (top-right) ── */}
      <DurationBadge dur={durationStr} />

      {/* ── Lottie pulse ring (hero only, while playing) ── */}
      {hero && isPlaying && (
        <div className="was-lottie-wrap">
          <Lottie animationData={PULSE_LOTTIE} loop style={{ width: "100%", height: "100%" }} />
        </div>
      )}

      {/* ── Play/Pause Toggle ── */}
      <button 
        className={`was-toggle-btn ${hero ? "was-toggle-hero" : "was-toggle-small"} ${!isPlaying ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        onClick={handleToggle}
        aria-label={isPlaying ? "Pause silent preview" : "Play silent preview"}
      >
        {isPlaying ? (
          <Pause size={hero ? 24 : 16} className="text-gray-900" />
        ) : (
          <Play size={hero ? 24 : 16} className="text-gray-900 translate-x-[2px]" />
        )}
      </button>

      {/* ── Title overlay (bottom, always visible) ── */}
      {video.title && (
        <div className="was-title-overlay">{video.title}</div>
      )}
    </div>
  );
});
VideoCard.displayName = "VideoCard";

/* ────────────────────────────────────────────────────────────────────────
   LIGHTBOX — full audio, GSAP animated open/close
──────────────────────────────────────────────────────────────────────── */
const Lightbox = memo(({ video, onClose }) => {
  const vidRef   = useRef(null);
  const overlayRef = useRef(null);
  const boxRef   = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    gsap.fromTo(overlayRef.current,
      { opacity: 0 }, { opacity: 1, duration: 0.22, ease: "power2.out" }
    );
    gsap.fromTo(boxRef.current,
      { scale: 0.88, opacity: 0, y: 32 },
      { scale: 1,    opacity: 1, y: 0,  duration: 0.38, ease: "back.out(1.5)" }
    );
    if (vidRef.current) vidRef.current.play().catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    gsap.to(boxRef.current,   { scale: 0.88, opacity: 0, y: 32, duration: 0.22, ease: "power2.in", onComplete: onClose });
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.22 });
    if (vidRef.current) { vidRef.current.pause(); vidRef.current.currentTime = 0; }
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) handleClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.9)", backdropFilter: "blur(14px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div ref={boxRef} style={{
        position: "relative", width: "100%", maxWidth: 960,
        background: "#0d0d0d", borderRadius: 20, overflow: "hidden",
        boxShadow: "0 40px 100px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.07)",
      }}>
        <button onClick={handleClose} className="was-lb-btn" style={{ right: 14 }} aria-label="Close">
          <X size={18} color="#fff" />
        </button>
        <button
          onClick={() => { if (vidRef.current) vidRef.current.muted = !vidRef.current.muted; setMuted(m => !m); }}
          className="was-lb-btn" style={{ right: 62 }}
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={16} color="#fff" /> : <Volume2 size={16} color="#fff" />}
        </button>

        <video
          ref={vidRef}
          src={video.videoUrl}
          controls playsInline preload="metadata"
          style={{ width: "100%", display: "block", maxHeight: "82vh", background: "#000" }}
        />
        {video.title && (
          <div style={{ padding: "12px 20px", color: "#999", fontSize: 13, fontWeight: 600,
            borderTop: "1px solid rgba(255,255,255,0.07)" }}>
            {video.title}
          </div>
        )}
      </div>
    </div>
  );
});
Lightbox.displayName = "Lightbox";

/* ────────────────────────────────────────────────────────────────────────
   MAIN SECTION
──────────────────────────────────────────────────────────────────────── */
const WatchAndShop = () => {
  const [videos,      setVideos]      = useState([]);
  const [header,      setHeader]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [sectionVisible, setSectionVisible] = useState(false); // drives autoplay

  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 2);
    }
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll, videos]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
  };
  const scrollRight = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
  };

  /* Per-card video refs (max 5) */
  const vidRefs = useRef([
    { current: null }, { current: null }, { current: null },
    { current: null }, { current: null },
  ]);

  /* Fetch */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getWatchAndShopVideos();
      if (!cancelled) { 
        setVideos(result.videos || []); 
        setHeader(result.header || { tag: "VIDEOS", title: "Watch and shop", description: "Real product walkthroughs, reviews and side-by-side demos." });
        setLoading(false); 
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* IntersectionObserver: plays all when section visible, pauses when not */
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => setSectionVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [loading]);

  /* GSAP — 3D pop entrance on scroll */
  useGSAP(
    () => {
      if (loading || !sectionRef.current) return;

      /* Header reveal */
      gsap.fromTo(".was-hdr",
        { y: 30, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.11, ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 84%" },
        }
      );

      /* 3D pop: cards burst toward viewer from behind the screen */
      gsap.fromTo(".was-card",
        {
          opacity: 0,
          scale: 0.65,
          rotateX: 20,
          rotateY: -12,
          z: -120,
          transformOrigin: "center center",
        },
        {
          opacity: 1,
          scale: 1,
          rotateX: 0,
          rotateY: 0,
          z: 0,
          duration: 0.75,
          stagger: {
            each: 0.1,
            from: "start",
          },
          ease: "back.out(1.4)",
          clearProps: "rotateX,rotateY,z",
          scrollTrigger: { trigger: ".was-grid", start: "top 80%" },
        }
      );
    },
    { scope: sectionRef, dependencies: [loading] }
  );

  const handleOpen  = useCallback((v) => setActiveVideo(v), []);
  const handleClose = useCallback(() => setActiveVideo(null), []);

  if (!loading && (videos.length === 0 || header?.isActive === false)) return null;

  const sorted    = [...videos].sort((a, b) => a.order - b.order);
  const hero      = sorted.find((v) => v.isHero) || sorted[0];
  const smalls    = sorted.filter((v) => v._id !== hero?._id).slice(0, 4);
  const allCards  = hero ? [hero, ...smalls] : smalls; // index → vidRef

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-slate-900 dark:bg-[#0d0d0d] pt-8 pb-14 sm:pt-10 sm:pb-16"
      aria-label="Watch and Shop"
    >
      {/* ── Premium Backdrop ── */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.12)_0%,transparent_70%)] dark:bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.1)_0%,transparent_70%)]" />

      {/* ── CSS ── */}
      <style>{`
        @keyframes was-shimmer-slide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* Grid */
        .was-grid {
          display: none;
          perspective: 1000px;
        }
        .was-smalls-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (min-width: 768px) {
          .was-grid {
            display: grid;
            grid-template-columns: 3fr 2fr 2fr;
            height: 400px;
            gap: 14px;
          }
          .was-hero-col  { grid-column: 1; height: 100%; }
          .was-smalls-col { grid-column: 2 / span 2; height: 100%; }
          .was-smalls-grid {
            height: 100%;
            grid-template-rows: 1fr 1fr;
          }
        }
        @media (min-width: 1280px) { .was-grid { height: 440px; } }

        /* Shimmer */
        .was-shimmer { background:#334155; border-radius:20px; overflow:hidden; position:relative; width:100%; height:100%; min-height:200px; }
        .dark .was-shimmer { background:#1c1c1c; }
        .was-shimmer-slide {
          position:absolute; inset:0;
          background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.04) 50%,transparent 100%);
          animation:was-shimmer-slide 1.5s infinite;
        }
        .dark .was-shimmer-slide {
          background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.04) 50%,transparent 100%);
        }

        /* Card */
        .was-card {
          position: relative;
          overflow: hidden;
          border-radius: 30px;
          background: #1e293b;
          cursor: pointer;
          width: 100%; height: 100%;
          min-height: 180px;
          transform-style: preserve-3d;
          transform: translateZ(0); /* fixes corner clipping in some browsers */
          will-change: transform, opacity;
          border: 1px solid rgba(255,255,255,0.1); 
          box-shadow: inset 0 4px 12px -2px rgba(255,255,255,0.3), inset 0 0 0 1px rgba(255,255,255,0.15), 0 10px 30px -10px rgba(0,0,0,0.5);
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .dark .was-card { 
          background: #1a1a1a; 
          border: 1px solid rgba(255,255,255,0.05); 
          box-shadow: inset 0 4px 12px -2px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(255,255,255,0.1), 0 10px 30px -10px rgba(0,0,0,0.5);
        }
        .was-card:hover {
          border-color: rgba(99,102,241,0.3);
          box-shadow: inset 0 4px 12px -2px rgba(255,255,255,0.4), inset 0 0 0 1px rgba(255,255,255,0.2), 0 20px 40px -10px rgba(99,102,241,0.2);
        }
        .dark .was-card:hover {
          border-color: rgba(99,102,241,0.4);
          box-shadow: inset 0 4px 12px -2px rgba(255,255,255,0.3), inset 0 0 0 1px rgba(255,255,255,0.15), 0 20px 40px -10px rgba(99,102,241,0.3);
        }
        .was-hero-cell  { min-height: 300px; }
        .was-small-cell { min-height: 0; }

        /* Mobile Slider */
        .was-mobile-slider {
          display: flex;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          gap: 0;
          scrollbar-width: none;
        }
        .was-mobile-slider::-webkit-scrollbar { display: none; }
        .was-mobile-slide {
          flex: 0 0 100%;
          scroll-snap-align: start;
          height: 300px;
        }
        .was-slider-btn {
          position: absolute; top: 50%; transform: translateY(-50%);
          z-index: 10; background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.35); border-radius: 50%;
          width: 40px; height: 40px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: white; transition: all 0.2s;
          box-shadow: 0 4px 15px rgba(0,0,0,0.5);
        }
        .was-slider-btn:hover { background: rgba(255,255,255,0.15); border-color: rgba(255,255,255,0.6); }
        .was-slider-btn.left { left: 10px; }
        .was-slider-btn.right { right: 10px; }
        .was-hero-cell  { min-height: 300px; }
        .was-small-cell { min-height: 0; }

        /* Scanline Effect */
        .was-scanline {
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.04) 50%, rgba(0,0,0,0.04));
          background-size: 100% 4px;
          z-index: 3; pointer-events: none;
          opacity: 0; transition: opacity 0.4s ease;
        }
        .dark .was-scanline {
          background: linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0) 50%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.2));
        }
        .was-card:hover .was-scanline { opacity: 1; }

        /* Media */
        .was-media {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
        }
        .was-card:hover .was-media {
          transform: scale(1.04);
        }

        /* Gradient overlay */
        .was-gradient-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, transparent 80%);
          z-index: 2; pointer-events: none;
        }

        /* Duration badge */
        .was-dur-badge {
          position: absolute; top: 12px; right: 12px;
          background: rgba(0,0,0,0.82); color: #fff;
          font-size: 11px; font-weight: 700; padding: 3px 10px;
          border-radius: 100px; letter-spacing: 0.04em;
          z-index: 4; backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.09);
        }

        /* Lottie pulse ring */
        .was-lottie-wrap {
          position: absolute;
          width: 110px; height: 110px;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none; z-index: 4;
        }

        /* Pause / Play toggle button */
        .was-toggle-btn {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          border: none; border-radius: 50%;
          background: #ffffff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; z-index: 6;
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
          transition: transform 0.18s ease, background 0.18s ease, opacity 0.22s ease;
          opacity: 1;
        }
        .was-toggle-btn:hover { transform: translate(-50%,-50%) scale(1.12); opacity: 1; background: #fff; }
        .was-toggle-hero  { width: 62px; height: 62px; }
        .was-toggle-small { width: 42px; height: 42px; }

        /* Title overlay at bottom */
        .was-title-overlay {
          position: absolute; bottom: 0; left: 0; right: 0;
          padding: 12px 14px 14px;
          font-size: 12px; font-weight: 700; color: #fff;
          text-shadow: 0 1px 6px rgba(0,0,0,0.8);
          z-index: 5; pointer-events: none;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        /* Lightbox control buttons */
        .was-lb-btn {
          position: absolute; top: 14px; z-index: 10;
          background: rgba(255,255,255,0.14); backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.15); border-radius: 50%;
          width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
        }

        /* Section tag pill */
        .was-section-tag {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(234, 179, 8, 0.06); border: 1px solid rgba(234, 179, 8, 0.3);
          border-radius: 100px; padding: 4px 14px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
          text-transform: uppercase; color: #eab308; margin-bottom: 14px;
        }
      `}</style>

      <div className="container mx-auto px-4 relative z-10">

        {/* ── Header ── */}
        <div style={{ marginBottom: 16 }}>
          <div className="was-hdr flex items-center flex-wrap gap-x-3 gap-y-1" style={{ marginBottom: 7 }}>
            {/* <div className="was-section-tag" style={{ marginBottom: 0 }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:"#eab308", display:"inline-block" }} />
              {header?.tag || "VIDEOS"}
            </div> */}
            <h2 className="text-white" style={{
              fontSize: "clamp(1.6rem,3.5vw,2.3rem)", fontWeight: 900,
              lineHeight: 1.12, margin: 0,
            }}>
              {header?.title || "Watch and shop"}
            </h2>
          </div>
          <p className="was-hdr text-slate-400 dark:text-[#666]" style={{ fontSize: 14, lineHeight: 1.65, maxWidth: 420 }}>
            {header?.description || "Real product walkthroughs, reviews and side-by-side demos."}
          </p>
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div className="was-grid hidden md:grid">
            <div className="was-hero-col"><Shimmer hero /></div>
            <div className="was-smalls-col">
              <div className="was-smalls-grid">
                <Shimmer /><Shimmer /><Shimmer /><Shimmer />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Grid */}
            <div className="was-grid hidden md:grid">
              {/* Hero */}
              {hero && (
                <div className="was-hero-col">
                  <VideoCard
                    video={hero}
                    hero
                    onClick={handleOpen}
                    autoplay={sectionVisible}
                    vidRef={vidRefs.current[0]}
                  />
                </div>
              )}

              {/* 4 small in 2×2 */}
              <div className="was-smalls-col">
                <div className="was-smalls-grid">
                  {smalls.map((v, i) => (
                    <VideoCard
                      key={v._id}
                      video={v}
                      hero={false}
                      onClick={handleOpen}
                      autoplay={sectionVisible}
                      vidRef={vidRefs.current[i + 1]}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Layout — single card slider */}
            <div className="md:hidden">
              <div className="relative">
                {/* Left arrow — only shown after scroll */}
                {canScrollLeft && (
                  <button onClick={scrollLeft} className="was-slider-btn left" aria-label="Scroll left">
                    <ChevronLeft size={22} />
                  </button>
                )}

                {/* Single-card horizontal slider (all cards) */}
                <div className="was-mobile-slider" ref={scrollContainerRef} onScroll={checkScroll}>
                  {allCards.map((v, i) => (
                    <div className="was-mobile-slide" key={v._id || i}>
                      <VideoCard
                        video={v}
                        hero={true}
                        onClick={handleOpen}
                        autoplay={sectionVisible}
                        vidRef={vidRefs.current[i]}
                      />
                    </div>
                  ))}
                </div>

                {/* Right arrow — shown when more cards to the right */}
                {canScrollRight && allCards.length > 0 && (
                  <button onClick={scrollRight} className="was-slider-btn right" aria-label="Scroll right">
                    <ChevronRight size={22} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Lightbox ── */}
      {activeVideo && <Lightbox video={activeVideo} onClose={handleClose} />}
    </section>
  );
};

export default WatchAndShop;
